import { z } from 'zod';
import '@apexdesigner/schema-tools';
import { belongsTo, options, references } from '@apexdesigner/schema-tools';

export const packageVersion = z
  .object({
    id: z.number().hidden().describe('Unique identifier'),

    version: z
      .string()
      .nullable()
      .optional()
      .describe('Version - The semver string, e.g. 1.4.2'),

    manifestJson: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe(
        'Manifest Json - The version document npm sent at publish, minus attachments and dist',
      ),

    readme: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe('Readme - Extracted at publish time for the package page'),

    shasum: z
      .string()
      .nullable()
      .optional()
      .describe('Shasum - SHA-1 hex of the tarball'),

    integrity: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Integrity - sha512 subresource integrity string of the tarball',
      ),

    publishedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('Published At - When this version was published'),

    publishedFromRepository: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Published From Repository - `owner/repo` of the GitHub workflow that published this version, when trusted publishing did\n\nThese four and Published By are mutually exclusive: a trusted\npublishing token has no user behind it, so Published By stays unset\nrather than pointing at a fabricated one. A version says either which\nrepository released it or which person did.',
      ),

    publishedFromWorkflow: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Published From Workflow - The workflow that published this version',
      ),

    publishedFromRunId: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Published From Run Id - The workflow run that published this version',
      ),

    actor: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Actor - The GitHub user who set the publishing run off; recorded because it is the first thing anyone asks, never used to decide anything',
      ),

    deprecatedMessage: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Deprecated Message - Set when this specific version is deprecated',
      ),

    source: options([
      {
        value: 'Local',
        name: 'Local',
        description: 'Published to this registry with npm publish',
      },
      {
        value: 'Upstream',
        name: 'Upstream',
        description: 'Copied from the upstream registry by the sync',
      },
    ])
      .nullable()
      .optional()
      .describe(
        'Source - Published here, or copied from the upstream registry by the sync',
      ),

    fileIndexJson: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe(
        "File Index Json - JSON array of the unpacked tarball's files: path, size, and whether the registry treats it as text\n\nWritten by the unpack, in the same update as Unpacked At, and the\nonly list a file request is validated against — a path that is not\nin the index is never turned into an object key.",
      ),

    unpackedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe(
        "Unpacked At - When this version's tarball was expanded into objects beside it\n\nUnset until the first request for one of the version's files.\nBecause it is written last, a crash mid-unpack leaves it unset and\nthe next request retries the whole unpack rather than serving half\na tree.",
      ),

    npmPackageId: z.number().hidden().describe('Foreign key to NpmPackage'),

    publishedById: z
      .number()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to User'),

    npmPackage: belongsTo('NpmPackage'),

    publishedBy: references('User'),
  })
  .describe('One published, immutable version of an npm package.')
  .as('PackageVersion');

export { packageVersion as packageVersionSchema };
